/**
 * ErrorListItem  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import {
	DOC_ERROR_SEVERITY,
	ICompileError,
	IDocumentError,
	IRuntimeError
} from "@adapptio/lib-blueprint";
import { ErrorListItemIcon } from "./ErrorListItemIcon";
import { ErrorListItemName } from "./ErrorListItemName";
import { ErrorListItemMessage } from "./ErrorListItemMessage";
import { ErrorListItemPath } from "./ErrorListItemPath";
import { ErrorListItemDetails } from "./ErrorListItemDetails";
import { ErrorListItemRange } from "./ErrorListItemRange";
import { ErrorListItemBadge } from "./ErrorListItemBadge";

export interface IErrorListItemProps<Err = IDocumentError | IRuntimeError | ICompileError> {
	error: Err;
	onClick?: (error: Err) => void;
	inset?: boolean;
	count?: number;
	className?: string;
}

const getSeverityModifier = (severity: DOC_ERROR_SEVERITY) =>
	({
		[DOC_ERROR_SEVERITY.ERROR]: "error",
		[DOC_ERROR_SEVERITY.WARNING]: "warning",
		[DOC_ERROR_SEVERITY.INFO]: "info",
		[DOC_ERROR_SEVERITY.HINT]: "hint"
	}[severity] || null);

export const ErrorListItem = React.memo<IErrorListItemProps>(
	({ error, onClick, count, inset, className }) => {
		const baseClass = BEM("edt-error-list", "item", {
			severity: getSeverityModifier(error.severity),
			inset
		});

		const handleClick = useCallback(() => {
			if (isFunction(onClick)) {
				onClick(error);
			}
		}, [ onClick ]);

		if (className) {
			baseClass.add(className);
		}

		return (
			<div className={baseClass.toClassName()} onClick={handleClick}>
				<ErrorListItemIcon severity={error.severity} />
				<div className={baseClass.element("item-content").toClassName()}>
					<div
						className={baseClass
							.element("item-row")
							.mod({ column: (error as IRuntimeError).details?.length > 1 })
							.toClassName()}
					>
						{count > 1 && <ErrorListItemBadge>{count}</ErrorListItemBadge>}
						<ErrorListItemMessage>
							{error.message}
							{(error as IRuntimeError).details?.length > 0 ? ":" : ""}
						</ErrorListItemMessage>
						{(error as IRuntimeError).details?.length && (
							<ErrorListItemDetails details={(error as IRuntimeError).details} />
						)}
					</div>
					<div className={baseClass.element("item-row").toClassName()}>
						{(error as IRuntimeError).modelPath?.length && (
							<ErrorListItemPath path={(error as IRuntimeError).modelPath} />
						)}
						{(error as IDocumentError).parsePath?.length && (
							<ErrorListItemPath path={(error as IDocumentError).parsePath} />
						)}
						<ErrorListItemName>({error.name})</ErrorListItemName>
					</div>
				</div>
				{(error as IDocumentError).range && (
					<ErrorListItemRange range={(error as IDocumentError).range} />
				)}
			</div>
		);
	}
);

ErrorListItem.displayName = "ErrorListItem";
